import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import ScrollToTop from "../../../../components/scrollToTop/scrollToTop";

function BatchVuelos() {
  const [data, setData] = useState([]);
  const [updateTrigger, setUpdateTrigger] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataPerPage, setDataPerPage] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const filteredData = data.filter((item) => {
    return searchValue
      ? item.flight.toUpperCase().trim().includes(searchValue.toUpperCase()) ||
          item.origin.toUpperCase().trim().includes(searchValue.toUpperCase())
      : true;
  });

  const indexOfLastData = currentPage * dataPerPage;
  const indexOfFirstData = indexOfLastData - dataPerPage;

  const currentData = filteredData.slice(indexOfFirstData, indexOfLastData);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const totalPageCount = Math.ceil(filteredData.length / dataPerPage);
  const pageNumbers = [];
  const maxPageNumbersToShow = 20;

  let startPage, endPage;
  if (totalPageCount <= maxPageNumbersToShow) {
    startPage = 1;
    endPage = totalPageCount;
  } else {
    startPage = Math.max(1, currentPage - Math.floor(maxPageNumbersToShow / 2));
    endPage = Math.min(totalPageCount, startPage + maxPageNumbersToShow - 1);

    if (endPage - startPage < maxPageNumbersToShow) {
      startPage = endPage - maxPageNumbersToShow + 1;
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  const showEntries = (e) => {
    setDataPerPage(e.target.value);
  };

  const handleSearch = async () => {
    const input = document.getElementById("searchInput");
    const inputValue = input.value.trim();
    setSearchValue(inputValue);
    setCurrentPage(1);

    const hasStart = !!startDate;
    const hasEnd = !!endDate;

    if (hasStart && !hasEnd) {
      Swal.fire("Error", "Debes seleccionar la fecha de fin", "error");
      return;
    }

    if (!hasStart && hasEnd) {
      Swal.fire("Error", "Debes seleccionar la fecha de inicio", "error");
      return;
    }

    if (hasStart && hasEnd) {
      if (new Date(startDate) > new Date(endDate)) {
        Swal.fire(
          "Error",
          "La fecha de inicio no puede ser mayor que la de fin",
          "error"
        );
        return;
      }

      if (!validateDateRange(startDate, endDate)) {
        Swal.fire(
          "Error",
          "El rango de fechas no puede ser mayor a 31 días",
          "error"
        );
        return;
      }

      await getData(true);
      return;
    }

    // Si solo hay texto (sin fechas)
    if (inputValue !== "") {
      // No llamamos getData, ya que ya se cargó todo en el useEffect inicial
      return;
    }

    // Si no hay fechas ni búsqueda
    Swal.fire(
      "Error",
      "Ingresa un término de búsqueda o selecciona un rango de fechas",
      "error"
    );
  };

  const formatDateForAPI = (dateString, isStart = true) => {
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(Date.UTC(year, month - 1, day)); // UTC exacta

    if (isStart) {
      return `${date.toISOString().split("T")[0]} 00:00:00`;
    } else {
      return `${date.toISOString().split("T")[0]} 23:59:59`;
    }
  };

  const validateDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30;
  };

  const getData = async (withDates = false) => {
    try {
      let url = `${process.env.REACT_APP_URL_BACKEND}/api/v1/batch_flights`;

      if (withDates && startDate && endDate) {
        if (!validateDateRange(startDate, endDate)) {
          Swal.fire(
            "Error",
            "El rango de fechas no puede ser mayor a 31 días",
            "error"
          );
          return;
        }
        const formattedStart = formatDateForAPI(startDate, true);
        const formattedEnd = formatDateForAPI(endDate, false);
        url += `?date_init=${formattedStart}&date_finish=${formattedEnd}`;
      }

      const res = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("token")}`,
        },
      });

      if (res.data.code === 1) {
        const sorted = res.data.data.sort((a, b) => {
          return new Date(b.created_at) - new Date(a.created_at); // de más nuevo a más viejo
        });

        setData(sorted);
      }
    } catch (error) {
      Swal.fire("Error", "Hubo un error", "error");
    }
  };

  const clearDateFilter = () => {
    setStartDate("");
    setEndDate("");
    setSearchValue("");
    getData();
    setCurrentPage(1);
  };

  const formatDateUTC = (dateString) => {
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const seconds = String(date.getUTCSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const formatDateHuman = (dateString) => {
    const date = new Date(dateString);

    return new Intl.DateTimeFormat("es-CO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "UTC", // clave: mostrar igual para todos sin importar zona
    }).format(date);
  };

  useEffect(() => {
    getData();
  }, [updateTrigger]);

  return (
    <>
      <ScrollToTop />
      <div className="table-responsive">
        <div
          className="dataTables_wrapper dt-bootstrap4 no-footer"
          id="datatables_wrapper"
        >
          <div className="row justify-content-between mb-2">
            <div className="col-sm-12 col-md-2">
              <div className="dataTables_length" id="dataTables_length">
                <label className="d-flex justify-content-end">
                  <span className="mr-2 p-2">Mostrar</span>
                  <select
                    name="datatables_length"
                    aria-controls="datatables_length"
                    className="custom-select custom-select-sm form-control-sm narrow-select p-2"
                    onChange={showEntries}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                  <span className="ml-2 p-2">Registros</span>
                </label>
              </div>
            </div>
            <div className="col-sm-12 col-md-9">
              <div className="datatables_filter" id="datatables_filter">
                <label className="d-flex justify-content-end">
                  <input
                    type="date"
                    id="start_date"
                    name="start_date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="form-control form-control-sm"
                    placeholder
                    aria-controls="datatables"
                  />

                  <span className="mr-2 mt1 p-2"></span>

                  <input
                    type="date"
                    id="end_date"
                    name="end_date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="form-control form-control-sm"
                    placeholder
                    aria-controls="datatables"
                  />
                  <span className="mr-2 mt1 p-2">Buscar</span>
                  <input
                    type="search"
                    id="searchInput"
                    className="form-control form-control-sm"
                    placeholder
                    aria-controls="datatables"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                  />
                  <button className="btn-amc-blue ml-2" onClick={handleSearch}>
                    Buscar
                  </button>
                  {(startDate || endDate) && (
                    <button
                      className="btn-amc-pink ml-2"
                      onClick={clearDateFilter}
                    >
                      Limpiar
                    </button>
                  )}
                </label>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <table
                className="table table-inverse table-hover table-striped table-bordered dataTable no-footer"
                role="grid"
                aria-describedby="datatables_info"
              >
                <thead>
                  <tr role="row">
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "81.2031px" }}
                    >
                      Id
                    </th>
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "178.203px" }}
                    >
                      Vuelo
                    </th>
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "180.766px" }}
                    >
                      Fecha
                    </th>
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "343.25px" }}
                    >
                      Origen
                    </th>
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "159.453px" }}
                    >
                      Destino
                    </th>
                    <th
                      className="sorting_disabled"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "161.125px" }}
                    >
                      Aerolínea
                    </th>
                    <th
                      className="sorting_disabled"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "161.125px" }}
                    >
                      Matrícula
                    </th>
                    <th
                      className="sorting_disabled"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "161.125px" }}
                    >
                      Code
                    </th>
                    <th
                      className="sorting_disabled"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "161.125px" }}
                    >
                      Mensaje
                    </th>
                    <th
                      className="sorting_disabled"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "161.125px" }}
                    >
                      Creación
                    </th>
                    <th
                      className="sorting_disabled"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "161.125px" }}
                    >
                      Edición
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.length > 0 ? (
                    currentData.map((user) => (
                      <tr key={user.id}>
                        <td>{user.id}</td>
                        <td>{user.flight}</td>
                        <td>{formatDateUTC(user.date)}</td>
                        <td>{user.origin}</td>
                        <td>{user.destination}</td>
                        <td>{user.airline}</td>
                        <td>{user.tail_number}</td>
                        <td>{user.code}</td>
                        <td>{user.message}</td>
                        <td>{formatDateHuman(user.created_at)}</td>
                        <td>{formatDateHuman(user.updated_at)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="text-center">
                        No hay datos
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="row">
            <nav aria-label="Page navigation example">
              <ul className="pagination">
                <li className="page-item">
                  <button
                    className="page-link"
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </button>
                </li>
                {pageNumbers.map((pageNumber) => (
                  <li className="page-item" key={pageNumber}>
                    <button
                      className="page-link"
                      onClick={() => paginate(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  </li>
                ))}
                <li className="page-item">
                  <button
                    className="page-link"
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === pageNumbers.length}
                  >
                    Siguiente
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </>
  );
}

export default BatchVuelos;
